import _ from "lodash";
import { AUTO_SMS_KEY } from "../../../constants";
import { getSentSMSByName } from "./getSentSMS";

const camelCaseToLowerCaseWithSpaces = (str) => str?.replace(/([A-Z])/g, " $1").toLowerCase() ?? "";

const getReceivedLogs = (received, sinceTs) =>
  Object.entries(received || {})
    .filter(([ts, text]) => {
      if (Number(ts) < sinceTs) {
        return false;
      }
      if (_.isNil(text)) {
        return false;
      }
      if (typeof text === "string") {
        return text.trim().length > 0;
      }
      return true;
    })
    .map((rec) => ({
      ts: Number(rec[0]),
      text: rec[1],
      patient: true,
    }));

// sms.sentHistory holds SMS from earlier appointments (moved there on cancel or rebook) in two
// shapes: { <ts>: { name, content, sentTime } } and the legacy { <smsName>: <unix seconds> }.
const getSentHistoryLogs = (sentHistory) =>
  _.flatMap(Object.entries(sentHistory || {}), ([key, value]) => {
    const isLegacy = !_.isPlainObject(value);
    const name = isLegacy ? key : _.get(value, "name", "");
    const ts = isLegacy ? Number(value) : Number(_.get(value, "sentTime", null)) || Number(key);
    if (!Number.isFinite(ts) || ts <= 0) {
      return [];
    }
    const content = isLegacy ? null : _.get(value, "content", null);
    return [
      {
        name,
        ts,
        text: content || `${camelCaseToLowerCaseWithSpaces(name)} SMS sent`.trim(),
      },
    ];
  });

const buildSMSLogs = (id, clientAppointments, smsType, sinceTs, includeHistory) => {
  let logs = [];
  const sms = _.get(
    _.find(clientAppointments, (ca) => ca.id === id),
    smsType,
    null
  );
  if (_.isEmpty(sms)) {
    return [];
  }
  const sentAutoSMS = sms?.sent;
  if (smsType === "sms" && sentAutoSMS) {
    const initialFirstSms = getSentSMSByName(sentAutoSMS, AUTO_SMS_KEY.INITIAL);
    const initialSecondSms = getSentSMSByName(sentAutoSMS, AUTO_SMS_KEY.INITIAL_SECOND);
    const initialThirdSms = getSentSMSByName(sentAutoSMS, AUTO_SMS_KEY.INITIAL_THIRD);
    const secondSms = getSentSMSByName(sentAutoSMS, AUTO_SMS_KEY.SECOND);
    const thirdSms = getSentSMSByName(sentAutoSMS, AUTO_SMS_KEY.THIRD);
    const fourthSms = getSentSMSByName(sentAutoSMS, AUTO_SMS_KEY.FOURTH);
    [initialFirstSms, initialSecondSms, initialThirdSms, secondSms, thirdSms, fourthSms].forEach((autoSMS) => {
      if (autoSMS && Number(autoSMS.sentTime) >= sinceTs) {
        logs.push({
          name: autoSMS.name,
          ts: autoSMS.sentTime,
          text: autoSMS.content || `${camelCaseToLowerCaseWithSpaces(autoSMS.name)} SMS sent`,
        });
      }
    });
  }

  if (smsType === "manual_sms") {
    const manualSentLogs = _.get(sms, "sent", []);
    manualSentLogs.forEach((log) => {
      logs.push({
        ts: log.time || log,
        text: log.msg || "Manual SMS sent",
      });
    });
  }

  if (_.has(sms, "received")) {
    logs = logs.concat(getReceivedLogs(sms.received, sinceTs));
  }

  if (includeHistory && smsType === "sms") {
    logs = logs.concat(
      getSentHistoryLogs(_.get(sms, "sentHistory", null)),
      getReceivedLogs(_.get(sms, "receivedHistory", null), sinceTs)
    );
  }
  logs = _.orderBy(logs, ["ts"], ["asc"]);
  return logs;
};

function getSMSLogs() {
  return (id, clientAppointments, smsType, sinceTs = 0) =>
    buildSMSLogs(id, clientAppointments, smsType, sinceTs, false);
}

// For the SMS Logs list only: also lists SMS and replies from earlier appointments
// (sms.sentHistory / sms.receivedHistory). The SMS status dots and the follow-up filters
// keep using getSMSLogs, so a cancelled or rebooked appointment starts with a clean status.
export const getSMSLogsWithHistory = (id, clientAppointments, smsType) =>
  buildSMSLogs(id, clientAppointments, smsType, 0, true);

export default getSMSLogs();
